import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useTheme } from 'styled-components';
import { Carat } from '../Icons';
import { RotateClockwise } from '../Icons/BaseIcon';
import { Container, IconWrapper, InvalidMessage, Main, Option, Options, PlaceholderOption, Selection, } from './styles';
const SEARCH_TERM_TIMEFRAME = 300;
export const Dropdown = ({ hidePlaceholderAsOption = false, isInvalid = false, isScrollable, messageWhenInvalid, onChange, onClick, onCloseDropdown, options, placeholderText, renderOptionText, renderSelectionText, selection, variant = 'box', tabIndex, ...rest }) => {
    const { color } = useTheme();
    const indexOfSelection = selection === undefined ? undefined : options.indexOf(selection);
    const [state, setState] = React.useState({
        indexOfHoveredOption: indexOfSelection === -1 ? undefined : indexOfSelection,
        isOpen: false,
    });
    const [lastSearchKeydownTimestamp, setLastSearchKeydownTimestamp] = React.useState(0);
    const [searchTerm, setSearchTerm] = React.useState('');
    const { indexOfHoveredOption, isOpen } = state;
    const someOptionIsHovered = indexOfHoveredOption !== undefined;
    const activeDescendantId = someOptionIsHovered || !!selection
        ? `${placeholderText}: ${someOptionIsHovered ? options[indexOfHoveredOption] : selection}`
        : undefined;
    function toggleIsOpen() {
        setState({
            ...state,
            isOpen: !isOpen,
        });
    }
    function incrementActiveOption() {
        const newIndex = indexOfHoveredOption === undefined
            ? 0
            : Math.min(options.length - 1, indexOfHoveredOption + 1);
        if (newIndex !== undefined) {
            const newOption = options[newIndex];
            if (newOption) {
                onChange(newOption);
                setState({
                    ...state,
                    indexOfHoveredOption: newIndex,
                });
            }
        }
    }
    function decrementActiveOption() {
        const newIndex = indexOfHoveredOption === undefined ? undefined : Math.max(0, indexOfHoveredOption - 1);
        if (newIndex !== undefined) {
            const newOption = options[newIndex];
            if (newOption) {
                onChange(newOption);
                setState({
                    ...state,
                    indexOfHoveredOption: newIndex,
                });
            }
        }
    }
    function openDropdown() {
        setState({
            ...state,
            isOpen: true,
        });
    }
    function escapeDropdown() {
        setState({
            ...state,
            indexOfHoveredOption: indexOfSelection,
            isOpen: false,
        });
        onCloseDropdown && onCloseDropdown();
    }
    function setIndexOfHoveredOption(index) {
        setState({
            ...state,
            indexOfHoveredOption: index,
        });
    }
    function selectHoveredOption() {
        setState({
            ...state,
            isOpen: false,
        });
    }
    function selectSearchedOption(key) {
        let localSearchTerm = searchTerm;
        const currentTimestamp = new Date().getTime();
        if (currentTimestamp - lastSearchKeydownTimestamp < SEARCH_TERM_TIMEFRAME) {
            localSearchTerm += key;
        }
        else {
            localSearchTerm = key;
        }
        const newIndex = options.findIndex(o => o.toLowerCase().startsWith(localSearchTerm.toLowerCase()));
        onChange(options[newIndex]);
        setState({
            ...state,
            indexOfHoveredOption: newIndex,
        });
        setLastSearchKeydownTimestamp(new Date().getTime());
        setSearchTerm(localSearchTerm);
    }
    function handleMainKeydown(event) {
        if (!isOpen && ['Enter', ' ', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
            return openDropdown();
        }
        if (isOpen && indexOfHoveredOption !== undefined && ['Enter', ' '].includes(event.key)) {
            const hoveredOption = options[indexOfHoveredOption];
            if (hoveredOption) {
                selectHoveredOption();
                return onChange(hoveredOption);
            }
        }
        if (event.key === 'Escape') {
            event.stopPropagation();
            return escapeDropdown();
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            return incrementActiveOption();
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            return decrementActiveOption();
        }
        const lowercaseLetterPressed = event.keyCode >= 97 && event.keyCode <= 122;
        const uppercaseLetterPressed = event.keyCode >= 65 && event.keyCode <= 90;
        if (lowercaseLetterPressed || uppercaseLetterPressed) {
            return selectSearchedOption(event.key);
        }
    }
    const selectionText = renderSelectionText?.(selection || placeholderText) ?? (selection || placeholderText);
    return (_jsxs(Container, { onClick: event => onClick && onClick(event), ...rest, children: [_jsxs(Main, { isInvalid, isOpen, variant, "aria-expanded": isOpen, "aria-haspopup": 'listbox', "aria-label": !!selection ? `${placeholderText}: ${selectionText}` : placeholderText, hasSelected: !!selection, onBlur: escapeDropdown, onClick: toggleIsOpen, onKeyDown: handleMainKeydown, role: 'button', tabIndex: tabIndex ?? 0, children: [_jsx(Selection, { isOpen, "aria-label": `${placeholderText}: ${!isOpen ? selectionText : ''}`, hasSelected: !!selection, children: selectionText }), _jsx(IconWrapper, { isOpen: isOpen, children: _jsx(Carat, { rotateClockwise: RotateClockwise.Quarter, stroke: color.deprecated.obsidian }) }), isOpen && (_jsxs(Options, { isScrollable, variant, "aria-activedescendant": activeDescendantId, "aria-label": activeDescendantId, role: 'listbox', children: [!!selection && !hidePlaceholderAsOption && (_jsx(PlaceholderOption, { onMouseOver: () => setIndexOfHoveredOption(undefined), children: placeholderText })), options.map(option => {
                                const indexOfThisOption = options.indexOf(option);
                                const isSelected = option === selection;
                                const isHovered = indexOfThisOption === indexOfHoveredOption;
                                const optionText = renderOptionText?.(option) ?? option;
                                return (_jsx(Option, { isSelected, isHovered, "aria-label": optionText, "aria-selected": isHovered, id: `${placeholderText}: ${optionText}`, onClick: () => {
                                        onChange(option);
                                        selectHoveredOption();
                                    }, onMouseOver: () => setIndexOfHoveredOption(indexOfThisOption), role: 'option', children: optionText }, option));
                            })] }))] }), isInvalid && !isOpen && _jsx(InvalidMessage, { children: messageWhenInvalid })] }));
};
//# sourceMappingURL=index.js.map